import React, { useState, useEffect } from 'react';
import { dailyPlansAPI } from '../services/api';
import '../styles/QuickLogModal.css';

const QuickLogModal = ({ task, onClose, onSaved }) => {
    const totalMinutes = Math.round((task.actualHours || 0) * 60);

    const [hours,     setHours]     = useState(Math.floor(totalMinutes / 60));
    const [minutes,   setMinutes]   = useState(totalMinutes % 60);
    const [notes,     setNotes]     = useState(task.notes || '');
    const [completed, setCompleted] = useState(task.completed || false);
    const [saving,    setSaving]    = useState(false);

    // Close on Escape key
    useEffect(() => {
        const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', handleKey);
        return () => window.removeEventListener('keydown', handleKey);
    }, [onClose]);

    const convertToDecimal = (h, m) => parseInt(h || 0) + (parseInt(m || 0) / 60);

    const formatHrs = (decimalHours) => {
        const totalMins = Math.round(decimalHours * 60);
        const h = Math.floor(totalMins / 60);
        const m = totalMins % 60;
        if (h === 0 && m === 0) return '0h';
        return m === 0 ? `${h}h` : `${h}h ${m}m`;
    };

    const actualDecimal = convertToDecimal(hours, minutes);
    const balance       = actualDecimal - (task.estimate || 0);

    const handleSave = async () => {
        if (saving) return;
        setSaving(true);
        try {
            await dailyPlansAPI.updateTaskLog(task.id, {
                actualHours: actualDecimal,
                notes,
                completed,
            });
            onSaved();   // refresh parent
            onClose();
        } catch (err) {
            alert(err.response?.data?.message || 'Failed to save log');
        } finally {
            setSaving(false);
        }
    };

    const project = task.backlogTask?.project?.name;

    return (
        <div className="qlm-overlay" onClick={onClose}>
            <div className="qlm-modal" onClick={e => e.stopPropagation()}>

                {/* ── Header ── */}
                <div className="qlm-header">
                    <div className="qlm-header-left">
                        <div className="qlm-label">Quick Log</div>
                        <div className="qlm-task-title">{task.title}</div>
                        {project && <div className="qlm-project">📁 {project}</div>}
                    </div>
                    <button className="qlm-close" onClick={onClose}>✕</button>
                </div>

                {/* ── Stats row ── */}
                <div className="qlm-stats">
                    <div className="qlm-stat">
                        <span className="qlm-stat-label">ESTIMATED</span>
                        <span className="qlm-stat-val">{formatHrs(task.estimate || 0)}</span>
                    </div>
                    <div className="qlm-stat">
                        <span className="qlm-stat-label">ACTUAL</span>
                        <span className="qlm-stat-val blue">{formatHrs(actualDecimal)}</span>
                    </div>
                    <div className="qlm-stat">
                        <span className="qlm-stat-label">BALANCE</span>
                        <span className={`qlm-stat-val ${balance > 0 ? 'red' : 'green'}`}>
                            {balance > 0 ? '+' : ''}{formatHrs(balance)}
                        </span>
                    </div>
                </div>

                {/* ── Body ── */}
                <div className="qlm-body">

                    {/* Time picker */}
                    <div className="qlm-field">
                        <label className="qlm-field-label">Hours Spent</label>
                        <div className="qlm-time-row">
                            <select
                                className="qlm-select"
                                value={hours}
                                onChange={e => setHours(parseInt(e.target.value))}
                            >
                                {[0,1,2,3,4].map(h => (
                                    <option key={h} value={h}>{String(h).padStart(2,'0')}</option>
                                ))}
                            </select>
                            <span className="qlm-colon">:</span>
                            <select
                                className="qlm-select"
                                value={minutes}
                                onChange={e => setMinutes(parseInt(e.target.value))}
                            >
                                {[0,15,30,45].map(m => (
                                    <option key={m} value={m}>{String(m).padStart(2,'0')}</option>
                                ))}
                            </select>
                            <span className="qlm-time-hint">hh : mm</span>
                        </div>
                    </div>

                    {/* Notes */}
                    <div className="qlm-field">
                        <label className="qlm-field-label">Work Notes</label>
                        <textarea
                            className="qlm-textarea"
                            placeholder="What did you work on? Any blockers?"
                            value={notes}
                            onChange={e => setNotes(e.target.value)}
                            rows={3}
                        />
                    </div>

                    {/* Completed */}
                    <label className="qlm-check-row">
                        <input
                            type="checkbox"
                            className="qlm-checkbox"
                            checked={completed}
                            onChange={e => setCompleted(e.target.checked)}
                        />
                        <span className="qlm-check-label">Mark task as completed</span>
                        {completed && <span className="qlm-check-badge">✓ Done</span>}
                    </label>
                </div>

                {/* ── Footer ── */}
                <div className="qlm-footer">
                    <button className="qlm-btn cancel" onClick={onClose}>Cancel</button>
                    <button
                        className="qlm-btn save"
                        onClick={handleSave}
                        disabled={saving}
                    >
                        {saving ? 'Saving…' : '💾 Save Log'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default QuickLogModal;